import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';

interface ModalData {
  title: string;
  description: string;
  features: string[];
  applications: string[];
  image: string;
}

export default function PortfolioSection() {
  const { t } = useTranslation();
  const [selectedModal, setSelectedModal] = useState<ModalData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const portfolioItems = [
    {
      title: t('portfolio.industrial'),
      image: "/media/other_picture/port1.png",
      icon: "ri-settings-3-line",
      modalData: {
        title: t('portfolio.industrial'),
        description: t('portfolio.industrial.description'),
        features: [
          t('portfolio.industrial.feature1'),
          t('portfolio.industrial.feature2'),
          t('portfolio.industrial.feature3'),
          t('portfolio.industrial.feature4'),
          t('portfolio.industrial.feature5')
        ],
        applications: [
          t('portfolio.industrial.app1'),
          t('portfolio.industrial.app2'),
          t('portfolio.industrial.app3'),
          t('portfolio.industrial.app4'),
          t('portfolio.industrial.app5')
        ],
        image: "/media/other_picture/port1.png"
      }
    },
    {
      title: t('portfolio.architectural'),
      image: "/media/other_picture/port2.png",
      icon: "ri-building-line",
      modalData: {
        title: t('portfolio.architectural'),
        description: t('portfolio.architectural.description'),
        features: [
          t('portfolio.architectural.feature1'),
          t('portfolio.architectural.feature2'),
          t('portfolio.architectural.feature3'),
          t('portfolio.architectural.feature4'),
          t('portfolio.architectural.feature5')
        ],
        applications: [
          t('portfolio.architectural.app1'),
          t('portfolio.architectural.app2'),
          t('portfolio.architectural.app3'),
          t('portfolio.architectural.app4'),
          t('portfolio.architectural.app5')
        ],
        image: "/media/other_picture/port2.png"
      }
    },
    {
      title: t('portfolio.furniture'),
      image: "/media/other_picture/port3.png",
      icon: "ri-armchair-line",
      modalData: {
        title: t('portfolio.furniture'),
        description: t('portfolio.furniture.description'),
        features: [
          t('portfolio.furniture.feature1'),
          t('portfolio.furniture.feature2'),
          t('portfolio.furniture.feature3'),
          t('portfolio.furniture.feature4'),
          t('portfolio.furniture.feature5')
        ],
        applications: [
          t('portfolio.furniture.app1'),
          t('portfolio.furniture.app2'),
          t('portfolio.furniture.app3'),
          t('portfolio.furniture.app4'),
          t('portfolio.furniture.app5')
        ],
        image: "/media/other_picture/port3.png"
      }
    },
    {
      title: t('portfolio.food'),
      image: "/media/other_picture/port4.png",
      icon: "ri-restaurant-line",
      modalData: {
        title: t('portfolio.food'),
        description: t('portfolio.food.description'),
        features: [
          t('portfolio.food.feature1'),
          t('portfolio.food.feature2'),
          t('portfolio.food.feature3'),
          t('portfolio.food.feature4'),
          t('portfolio.food.feature5')
        ],
        applications: [
          t('portfolio.food.app1'),
          t('portfolio.food.app2'),
          t('portfolio.food.app3'),
          t('portfolio.food.app4'),
          t('portfolio.food.app5')
        ],
        image: "/media/other_picture/port4.png"
      }
    }
  ];

  const handleItemClick = (modalData: ModalData) => {
    setSelectedModal(modalData);
  };

  const closeModal = () => {
    setSelectedModal(null);
  };

  return (
    <>
      <section 
        ref={sectionRef}
        id="portfolio" 
        className={`py-20 bg-blue-100 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {t('portfolio.title')}
            </h2>
            <p className="text-xl text-blue-500 font-medium">
              {t('portfolio.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {portfolioItems.map((item, index) => (
              <div 
                key={index} 
                className={`group cursor-pointer transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${400 + index * 150}ms` }}
                onClick={() => handleItemClick(item.modalData)}
              >
                <div className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300 object-top"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                    <div className="text-center text-white">
                      <i className={`${item.icon} w-12 h-12 flex items-center justify-center text-4xl mb-4 mx-auto`}></i>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 z-10 cursor-pointer"
              >
                <i className="ri-close-line w-6 h-6 flex items-center justify-center text-gray-600 text-xl"></i>
              </button>
              
              <img
                src={selectedModal.image}
                alt={selectedModal.title}
                className="w-full h-64 object-cover object-top"
              />
            </div>
            
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {selectedModal.title}
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {selectedModal.description}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    {t('portfolio.modal.keyFeatures')}
                  </h3>
                  <ul className="space-y-2">
                    {selectedModal.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line w-5 h-5 flex items-center justify-center text-blue-500 mt-0.5"></i>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    {t('portfolio.modal.applications')}
                  </h3>
                  <ul className="space-y-2">
                    {selectedModal.applications.map((application, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center text-blue-500 mt-0.5"></i>
                        <span className="text-gray-600">{application}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={closeModal}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('portfolio.modal.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
